/**
 * Restore-side counterpart to country-selector-modal/warehouse-cart-save.
 *
 * On page load (and on bfcache `pageshow`), check `localStorage` for a saved
 * cart snapshot keyed by the current `shipping_profile_warehouse_id`. If a
 * fresh entry exists AND the current cart is empty, restore items + applied
 * discount codes into the cart. The entry is sync-deleted from `localStorage`
 * BEFORE any async work to guarantee race-safety against concurrent
 * invocations (DOMContentLoaded + bfcache, or self-overlap).
 *
 * Trade-off: a transient `addToCart` failure means the entry is lost for that
 * 30-min window (no auto-retry). Plan §5 / §6 in WAREHOUSE-CART-PLAN.md.
 *
 * Constants and the DOM-based helpers are duplicated against
 * `country-selector-modal/` — must match — because Vite IIFE-wraps each
 * entry and forbids cross-entry imports.
 *
 * @module shared-cart/cart/warehouse-cart-preservation
 */

import {
  WAREHOUSE_CART_STORAGE_KEY,
  WAREHOUSE_CART_TTL_MS,
  WAREHOUSE_CART_VERSION,
  UserMessages,
} from '../constants.js';
import { addToCart, applyDiscountCode, refreshCartUI } from './api.js';
import { showRestoringState } from './sections.js';
import { displayError } from '../ui/error.js';
import { createSnapshotStore } from './snapshot.js';
import type { PreparedItem } from './api.js';
import type { SnapshotEntry } from './snapshot.js';

interface WarehouseCartEntry extends SnapshotEntry {
  savedAt: number;
  items: PreparedItem[];
  discountCodes: string[];
}

const store = createSnapshotStore({
  storage: 'local',
  key: WAREHOUSE_CART_STORAGE_KEY,
  mode: 'map',
  ttlMs: WAREHOUSE_CART_TTL_MS,
  version: WAREHOUSE_CART_VERSION,
});

/**
 * must match country-selector-modal/warehouse-warning.ts:getCurrentWarehouseId
 * @returns {string}
 */
function getCurrentWarehouseId(): string {
  return window.Shopify?.localization_custom?.country?.shipping_profile_warehouse_id || '';
}

/**
 * must match country-selector-modal/warehouse-warning.ts:isCartEmpty
 * Reads the server-rendered cart bubble. Must remain synchronous so the
 * entry-delete in step 4 of `tryRestoreWarehouseCart` happens without
 * yielding the event loop.
 * @returns {boolean}
 */
function isCartEmpty(): boolean {
  const el = document.querySelector<HTMLElement>(
    '#cart-icon-bubble .cart-count-bubble [data-count]',
  );
  if (!el) return true;
  const count = parseInt(el.dataset.count!, 10);
  return Number.isNaN(count) || count <= 0;
}

/**
 * Removes only the current warehouse's saved entry (other warehouses survive).
 * Used by the checkout-click handler — checkout means the user wants the
 * current cart, so any preserved snapshot under the current warehouse is now
 * stale.
 */
export function clearCurrentWarehouseEntry(): void {
  const warehouseId = getCurrentWarehouseId();
  if (!warehouseId) return;
  store.clear(warehouseId);
}

/**
 * Page-load restore entrypoint.
 *
 * Flow (per WAREHOUSE-CART-PLAN.md §5):
 *  1. Sync: read warehouseId; bail if falsy.
 *  2. Sync: sweep expired entries from the whole map.
 *  3. Sync: load entry for warehouseId (auto-evicts on invalid).
 *  4. Sync: bail if current cart non-empty (importCart race; user manually
 *     re-added items).
 *  5. Sync-delete the entry BEFORE any async work. If the delete write fails,
 *     bail entirely so the entry survives for next pageload.
 *  6. Async: addToCart → applyDiscountCode → refreshCartUI.
 *
 * @returns {Promise<void>}
 */
export async function tryRestoreWarehouseCart(): Promise<void> {
  const warehouseId = getCurrentWarehouseId();
  if (!warehouseId) return;

  // `get` sweeps expired siblings AND evicts an invalid target entry in a
  // single read+write pass.
  const entry = store.get(warehouseId) as WarehouseCartEntry | null;
  if (!entry) return;

  if (!isCartEmpty()) {
    // importCart populated the cart already, or user has stuff in cart.
    // Keep the entry — next clean visit will consume it.
    return;
  }

  // Corrupt entry (manual storage edit, or pre-guard saved write): nothing to
  // restore. Drop it from storage and bail before touching the UI.
  if (!entry.items?.length) {
    store.clear(warehouseId);
    return;
  }

  // Race guard: sync-delete BEFORE any async work so a concurrent invocation
  // (bfcache pageshow during slow cart-add) sees no entry and bails.
  const cleared = store.clear(warehouseId);
  if (!cleared) {
    console.error('[Shared Cart] Failed to sync-clear warehouse entry; aborting restore');
    return;
  }

  showRestoringState();

  let itemsRestored = false;
  try {
    const result = await addToCart(entry.items, { render: false });
    if (result?.errors) {
      console.error('[Shared Cart] Warehouse cart add returned errors:', result.errors);
      displayError(UserMessages.RESTORE_FAILED);
    } else {
      itemsRestored = true;
    }
  } catch (e) {
    console.error('[Shared Cart] Failed to restore warehouse cart items:', e);
    displayError(UserMessages.RESTORE_FAILED);
  }

  // Mirror backup.ts: only attempt discount apply when items actually landed.
  // A failed addToCart leaves an empty cart — applying codes would guarantee a
  // Shopify reject and a useless extra request.
  if (itemsRestored && entry.discountCodes?.length) {
    try {
      await applyDiscountCode(entry.discountCodes.join(','), { render: false });
    } catch (e) {
      console.warn('[Shared Cart] Failed to restore warehouse discount codes:', e);
    }
  }

  try {
    await refreshCartUI();
  } catch (e) {
    console.warn('[Shared Cart] Failed to refresh cart UI after warehouse restore:', e);
  }
}

let _checkoutClickAttached = false;
/**
 * Attaches a single capture-phase delegated click handler on `document` that
 * wipes the current-warehouse entry whenever a `button[name="checkout"]` is
 * clicked (catches `#checkout`, `#CartDrawer-Checkout`, the cart-notification
 * button, plus any future buttons sharing the name). Idempotent — repeat
 * calls are no-ops.
 */
export function attachCheckoutClickClearing(): void {
  if (_checkoutClickAttached) return;
  _checkoutClickAttached = true;
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target as (EventTarget & { closest?: Element['closest'] }) | null;
      if (!target || typeof target.closest !== 'function') return;
      const btn = target.closest('button[name="checkout"]');
      if (!btn) return;
      clearCurrentWarehouseEntry();
    },
    true, // capture phase — runs before form-submit
  );
}
